/* DzipMart — shared buyer location helper.
 *
 * Single source of truth for buyer coordinates across every liquid section that
 * renders marketplace listings. Uses navigator.geolocation once, caches for 1h
 * in localStorage, and fires "dz:buyerlocation" on window when it changes so
 * grids can re-render distance badges without re-fetching.
 *
 * API:
 *   DzBuyerLoc.get()              -> { lat, lng, ts } | null
 *   DzBuyerLoc.init()             -> kicks off geolocation prompt (idempotent)
 *   DzBuyerLoc.distanceKm(a, b)   -> Haversine km, numbers only
 *   DzBuyerLoc.formatDistance(km) -> "2.3 km away" / "<1 km away"
 *   DzBuyerLoc.queryParams()      -> "&buyer_lat=X&buyer_lng=Y" or ""
 *   DzBuyerLoc.clear()            -> wipe stored coordinates, re-prompt next init()
 *
 * Consumers listen for the event:
 *   window.addEventListener("dz:buyerlocation", () => reRenderCards());
 */
(function () {
  "use strict";
  if (window.DzBuyerLoc) return; // don't redefine if another script already loaded us

  var STORAGE_KEY = "dz_buyer_loc";
  var TTL_MS = 60 * 60 * 1000; // 1 hour — long enough to not nag, short enough to follow travel
  var EVENT_NAME = "dz:buyerlocation";
  var cached = null;

  function readCache() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || Date.now() - parsed.ts > TTL_MS) return null;
      if (typeof parsed.lat !== "number" || typeof parsed.lng !== "number") return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(loc) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch (e) {}
  }

  function setLocation(loc) {
    cached = loc;
    writeCache(loc);
    try { window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: loc })); } catch (e) {}
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
    var R = 6371;
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
          * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function formatDistance(km) {
    if (km == null || !isFinite(km)) return "";
    if (km < 1) return "<1 km away";
    if (km < 10) return km.toFixed(1) + " km away";
    return Math.round(km) + " km away";
  }

  // Reverse geocode via OpenStreetMap Nominatim — free, no key, usage-policy capped at 1 req/s.
  // Returns a readable locality label like "Kondapur, Hyderabad" so the chip never shows raw coordinates.
  function reverseGeocode(lat, lng) {
    var url = "https://nominatim.openstreetmap.org/reverse?format=json&lat=" + lat + "&lon=" + lng + "&zoom=12&accept-language=en";
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.address) return null;
        var a = data.address;
        // Prefer the most specific locality name the user would recognize.
        var primary = a.suburb || a.neighbourhood || a.village || a.town || a.city_district || a.city || a.state_district;
        var secondary = a.city || a.state_district || a.state;
        if (primary && secondary && primary.toLowerCase() !== secondary.toLowerCase()) {
          return primary + ", " + secondary;
        }
        return primary || secondary || (a.state || null);
      })
      .catch(function () { return null; });
  }

  function init() {
    if (!cached) cached = readCache();
    if (cached) return Promise.resolve(cached);
    if (!navigator.geolocation) return Promise.resolve(null);
    return new Promise(function (resolve) {
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          var loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
          setLocation(loc);
          resolve(loc);
          // Fire-and-forget label lookup — when it returns, update the stored loc + re-fire
          // the event so the floating chip swaps coordinates for a readable name.
          reverseGeocode(loc.lat, loc.lng).then(function (label) {
            if (label) {
              var updated = { lat: loc.lat, lng: loc.lng, ts: loc.ts, label: label, source: "geolocation" };
              setLocation(updated);
            }
          });
        },
        function () { resolve(null); },
        { timeout: 6000, maximumAge: 300000 }
      );
    });
  }

  function queryParams() {
    var loc = cached || readCache();
    if (!loc) return "";
    return "&buyer_lat=" + encodeURIComponent(loc.lat) + "&buyer_lng=" + encodeURIComponent(loc.lng);
  }

  function clear() {
    cached = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    try { window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null })); } catch (e) {}
  }

  // Resolve a pincode → lat/lng via the public api.postalpincode.in endpoint. Falls
  // back gracefully if the call fails — caller should handle the null case.
  function lookupPincode(pincode) {
    return fetch("https://api.postalpincode.in/pincode/" + encodeURIComponent(pincode), {
      headers: { Accept: "application/json" },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        // Shape: [ { Status: "Success", PostOffice: [ { Latitude, Longitude, District, ... } ] } ]
        if (!Array.isArray(data) || !data[0] || !Array.isArray(data[0].PostOffice)) return null;
        var po = data[0].PostOffice[0];
        if (!po) return null;
        // Many pincodes lack lat/lng on this API; fall through to a rough district centroid
        // via the District field. We keep the pincode label in the cache so the UI can show it.
        var lat = parseFloat(po.Latitude);
        var lng = parseFloat(po.Longitude);
        if (!isFinite(lat) || !isFinite(lng)) return { label: po.District + ", " + po.State, pincode: pincode };
        return { lat: lat, lng: lng, label: po.District + ", " + po.State, pincode: pincode };
      })
      .catch(function () { return null; });
  }

  function setByPincode(pincode) {
    return lookupPincode(pincode).then(function (resolved) {
      if (!resolved) return null;
      if (resolved.lat && resolved.lng) {
        var loc = { lat: resolved.lat, lng: resolved.lng, ts: Date.now(), label: resolved.label, pincode: resolved.pincode, source: "pincode" };
        setLocation(loc);
        return loc;
      }
      return null;
    });
  }

  // Minimal modal for changing the location. Rendered lazily when openWidget() is called.
  function openWidget() {
    if (document.getElementById("dz-buyerloc-modal")) return;
    var modal = document.createElement("div");
    modal.id = "dz-buyerloc-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.style.cssText = "position:fixed;inset:0;background:rgba(20,14,8,.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;backdrop-filter:blur(3px);font-family:inherit;";
    var current = cached || readCache();
    var currentLabel = current && current.label
      ? current.label
      : (current && current.pincode
        ? ("PIN " + current.pincode)
        : (current ? "Your area (resolving…)" : "Not set"));
    modal.innerHTML = [
      '<div style="background:#fff;border-radius:14px;max-width:360px;width:100%;padding:22px;box-shadow:0 18px 48px rgba(0,0,0,.2);">',
      '  <div style="font-family:Fraunces,serif;font-size:20px;font-weight:700;color:#2b1b0c;margin-bottom:4px;">Set your location</div>',
      '  <div style="font-size:12px;color:#6d5a49;margin-bottom:14px;">Products show distance from this location</div>',
      '  <div style="font-size:11px;color:#8b7355;margin-bottom:4px;">Current: <b>' + (currentLabel) + '</b></div>',
      '  <button id="dz-loc-geo" style="width:100%;padding:11px;background:#2874f0;color:#fff;border:none;border-radius:8px;font-weight:700;font-size:13px;cursor:pointer;margin-bottom:10px;">📍 Use my device location</button>',
      '  <div style="display:flex;align-items:center;gap:8px;margin:8px 0;"><div style="flex:1;height:1px;background:#e2e8f0;"></div><span style="font-size:11px;color:#8b7355;">OR</span><div style="flex:1;height:1px;background:#e2e8f0;"></div></div>',
      '  <label style="display:block;font-size:11px;color:#6d5a49;margin-bottom:4px;">Enter pincode</label>',
      '  <div style="display:flex;gap:6px;margin-bottom:6px;">',
      '    <input id="dz-loc-pin" type="text" inputmode="numeric" maxlength="6" placeholder="500084" style="flex:1;padding:9px 12px;border:1px solid #e2e8f0;border-radius:8px;font-size:14px;">',
      '    <button id="dz-loc-pinset" style="padding:0 14px;background:#2b1b0c;color:#fff;border:none;border-radius:8px;font-size:13px;cursor:pointer;">Set</button>',
      '  </div>',
      '  <div id="dz-loc-msg" style="font-size:11px;color:#b42318;min-height:14px;margin:4px 0 14px;"></div>',
      '  <div style="display:flex;justify-content:flex-end;gap:8px;">',
      '    <button id="dz-loc-clear" style="background:none;border:1px solid #e2e8f0;padding:7px 12px;border-radius:7px;font-size:12px;color:#6d5a49;cursor:pointer;">Clear</button>',
      '    <button id="dz-loc-close" style="background:none;border:1px solid #e2e8f0;padding:7px 12px;border-radius:7px;font-size:12px;color:#6d5a49;cursor:pointer;">Close</button>',
      '  </div>',
      '</div>'
    ].join("");
    document.body.appendChild(modal);
    var close = function () { if (modal.parentNode) modal.parentNode.removeChild(modal); };
    modal.addEventListener("click", function (e) { if (e.target === modal) close(); });
    document.getElementById("dz-loc-close").addEventListener("click", close);
    document.getElementById("dz-loc-clear").addEventListener("click", function () { clear(); close(); });
    document.getElementById("dz-loc-geo").addEventListener("click", function () {
      var msg = document.getElementById("dz-loc-msg");
      msg.textContent = "Requesting location…";
      msg.style.color = "#8b7355";
      if (!navigator.geolocation) { msg.textContent = "Your browser doesn't support location."; msg.style.color = "#b42318"; return; }
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now(), source: "geolocation" });
          close();
        },
        function (e) { msg.textContent = e.message || "Location permission denied."; msg.style.color = "#b42318"; },
        { timeout: 6000 }
      );
    });
    document.getElementById("dz-loc-pinset").addEventListener("click", function () {
      var pin = (document.getElementById("dz-loc-pin").value || "").trim();
      var msg = document.getElementById("dz-loc-msg");
      if (!/^\d{6}$/.test(pin)) { msg.textContent = "Enter a valid 6-digit pincode."; msg.style.color = "#b42318"; return; }
      msg.textContent = "Looking up pincode…"; msg.style.color = "#8b7355";
      setByPincode(pin).then(function (loc) {
        if (!loc) { msg.textContent = "Couldn't resolve that pincode."; msg.style.color = "#b42318"; return; }
        close();
      });
    });
    // Autofocus pincode so keyboard users can type immediately.
    setTimeout(function () { var el = document.getElementById("dz-loc-pin"); if (el) el.focus(); }, 40);
  }

  cached = readCache();

  window.DzBuyerLoc = {
    get: function () { return cached || readCache(); },
    init: init,
    distanceKm: haversineKm,
    formatDistance: formatDistance,
    queryParams: queryParams,
    clear: clear,
    setByPincode: setByPincode,
    openWidget: openWidget
  };

  // Kick off the geolocation prompt as soon as the script loads — no harm in
  // getting it early, and listings render faster when it resolves before fetch.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(); });
  } else {
    init();
  }

  // Global delegate: any element with [data-dz-change-location] opens the widget.
  // Makes it trivial for any liquid section to add a "Change location" link.
  document.addEventListener("click", function (e) {
    var el = e.target.closest && e.target.closest("[data-dz-change-location]");
    if (!el) return;
    e.preventDefault();
    openWidget();
  });

  function initNearbyShopsMap() {
    var path = location.pathname || "";
    if (path.indexOf("/pages/shops") !== 0) return;

    var API_BASE = "https://dzipmart-api.onrender.com/v1/marketplace/shops";
    var leafletPromise = null;
    var mapState = { map: null, markers: [], buyerMarker: null, buyerCircle: null, debounce: null };

    function injectMapStyles() {
      if (document.getElementById("dz-nearby-shops-map-style")) return;
      var style = document.createElement("style");
      style.id = "dz-nearby-shops-map-style";
      style.textContent = [
        ".dz-shops-map-shell{margin-top:16px;border:1px solid #e5e7eb;border-radius:24px;overflow:hidden;background:#fff;box-shadow:0 10px 28px rgba(15,23,42,.05)}",
        ".dz-shops-map-toolbar{display:flex;justify-content:space-between;align-items:center;gap:16px;padding:14px 16px;border-bottom:1px solid #e8edf4;background:linear-gradient(135deg,#f9fbff,#f6f2ea);flex-wrap:wrap}",
        ".dz-shops-map-kicker{font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:#6b7280;font-weight:800;margin-bottom:4px}",
        ".dz-shops-map-status{font-size:13px;color:#334155;font-weight:600}",
        ".dz-shops-map-actions{display:flex;align-items:center;gap:10px;flex-wrap:wrap}",
        ".dz-shops-map-cta,.dz-shops-map-link{border:none;text-decoration:none;background:#2563eb;color:#fff;font-size:11px;font-weight:700;padding:8px 13px;border-radius:999px;cursor:pointer;box-shadow:0 10px 18px rgba(37,99,235,.18)}",
        ".dz-shops-map-link{background:#111827;box-shadow:0 10px 18px rgba(17,24,39,.14)}",
        ".dz-shops-map-canvas{height:320px;background:#eef2f7;position:relative}",
        ".dz-shops-map-empty{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;text-align:center;color:#64748b;font-size:13px;padding:18px;z-index:401}",
        ".dz-buyer-map-marker{background:transparent;border:none}",
        ".dz-buyer-map-marker span{display:block;width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 4px rgba(37,99,235,.18)}",
        ".dz-shops-map-popup strong{display:block;font-size:13px;color:#111827;margin-bottom:3px}",
        ".dz-shops-map-popup div{font-size:12px;color:#475569}",
        ".dz-shops-map-popup a{color:#2563eb;font-weight:700;text-decoration:none}"
      ].join("");
      document.head.appendChild(style);
    }

    function loadLeaflet() {
      if (window.L && window.L.map) return Promise.resolve(window.L);
      if (leafletPromise) return leafletPromise;
      if (!document.getElementById("dz-leaflet-css")) {
        var css = document.createElement("link");
        css.id = "dz-leaflet-css";
        css.rel = "stylesheet";
        css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        css.crossOrigin = "";
        document.head.appendChild(css);
      }
      if (!document.getElementById("dz-leaflet-js")) {
        var script = document.createElement("script");
        script.id = "dz-leaflet-js";
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
        script.defer = true;
        script.crossOrigin = "";
        document.head.appendChild(script);
      }
      leafletPromise = new Promise(function (resolve) {
        var tries = 0;
        (function waitForLeaflet() {
          tries += 1;
          if (window.L && window.L.map) { resolve(window.L); return; }
          if (tries > 120) { resolve(null); return; }
          setTimeout(waitForLeaflet, 150);
        })();
      });
      return leafletPromise;
    }

    function escHtml(value) {
      var el = document.createElement("div");
      el.textContent = value == null ? "" : String(value);
      return el.innerHTML;
    }

    function normalizeName(name) {
      var raw = String(name || "").trim();
      if (!raw) return "DzipMart Seller";
      var weirdPossessive = raw.match(/^(.+s)'s Store$/i);
      if (weirdPossessive && weirdPossessive[1]) return weirdPossessive[1];
      return raw;
    }

    function googleMapsUrl(lat, lng, label) {
      var query = lat + "," + lng + (label ? " (" + label + ")" : "");
      return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(query);
    }

    function ensureShell() {
      injectMapStyles();
      var shell = document.querySelector(".dz-shops-map-shell");
      var legacy = document.querySelector(".dz-shops-map");
      if (!shell && legacy) {
        legacy.className = "dz-shops-map-shell";
        legacy.removeAttribute("onclick");
        legacy.innerHTML = [
          '<div class="dz-shops-map-toolbar">',
          '  <div>',
          '    <div class="dz-shops-map-kicker">Nearby seller map</div>',
          '    <div class="dz-shops-map-status" data-dz-shops-map-status>Shops pinned on map - sorted by distance</div>',
          '  </div>',
          '  <div class="dz-shops-map-actions">',
          '    <button type="button" class="dz-shops-map-cta" data-dz-change-location>Change location</button>',
          '    <a class="dz-shops-map-link" data-dz-shops-map-link href="#" target="_blank" rel="noopener" hidden>Open in Google Maps ↗</a>',
          '  </div>',
          '</div>',
          '<div class="dz-shops-map-canvas" data-dz-shops-map-canvas aria-label="Nearby shops map"></div>'
        ].join("");
        shell = legacy;
      }
      if (!shell) return null;
      return {
        canvas: shell.querySelector("[data-dz-shops-map-canvas]"),
        status: shell.querySelector("[data-dz-shops-map-status]"),
        link: shell.querySelector("[data-dz-shops-map-link]")
      };
    }

    function setStatus(refs, message) {
      if (refs && refs.status) refs.status.textContent = message || "Nearby shops map";
    }

    function setOverlay(refs, message) {
      if (!refs || !refs.canvas) return;
      var overlay = refs.canvas.querySelector(".dz-shops-map-empty");
      if (!message) {
        if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
        return;
      }
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = "dz-shops-map-empty";
        refs.canvas.appendChild(overlay);
      }
      overlay.textContent = message;
    }

    function clearLayers() {
      if (!mapState.map) return;
      mapState.markers.forEach(function (marker) {
        try { mapState.map.removeLayer(marker); } catch (e) {}
      });
      mapState.markers = [];
      if (mapState.buyerMarker) {
        try { mapState.map.removeLayer(mapState.buyerMarker); } catch (e) {}
        mapState.buyerMarker = null;
      }
      if (mapState.buyerCircle) {
        try { mapState.map.removeLayer(mapState.buyerCircle); } catch (e) {}
        mapState.buyerCircle = null;
      }
    }

    function currentParams() {
      var q = document.querySelector("[data-dz-shop-search]");
      var city = document.querySelector("[data-dz-shop-city]");
      var category = document.querySelector("[data-dz-shop-category]");
      var sort = document.querySelector("[data-dz-shop-sort]");
      var hideEmpty = document.querySelector("[data-dz-shop-hide-empty]");
      var params = [
        "include_empty=" + ((hideEmpty && hideEmpty.checked) ? "false" : "true"),
        "limit=100",
        "sort=" + encodeURIComponent((sort && sort.value) || ((cached || readCache()) ? "distance" : "products"))
      ];
      if (q && q.value) params.push("q=" + encodeURIComponent(String(q.value).trim()));
      if (city && city.value) params.push("city=" + encodeURIComponent(city.value));
      if (category && category.value) params.push("category_slug=" + encodeURIComponent(category.value));
      var buyer = queryParams();
      if (buyer) params.push(buyer.replace(/^&/, ""));
      return params.join("&");
    }

    function renderMap(items) {
      var refs = ensureShell();
      if (!refs || !refs.canvas) return;
      var shops = (items || []).filter(function (shop) {
        return shop && shop.lat != null && shop.lng != null && isFinite(shop.lat) && isFinite(shop.lng);
      });
      if (!shops.length) {
        clearLayers();
        setOverlay(refs, "No shop coordinates available for this view yet.");
        if (refs.link) refs.link.hidden = true;
        setStatus(refs, "No mapped shops for the current filters");
        return;
      }
      loadLeaflet().then(function (L) {
        if (!L) {
          setOverlay(refs, "Map failed to load. You can still browse the seller cards below.");
          setStatus(refs, "Map unavailable right now");
          if (refs.link) refs.link.hidden = true;
          return;
        }
        setOverlay(refs, "");
        if (!mapState.map) {
          mapState.map = L.map(refs.canvas, { zoomControl: true, scrollWheelZoom: false }).setView([17.437, 78.448], 11);
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: "&copy; OpenStreetMap contributors"
          }).addTo(mapState.map);
        }
        clearLayers();
        var bounds = [];
        shops.forEach(function (shop) {
          var lat = parseFloat(shop.lat);
          var lng = parseFloat(shop.lng);
          if (!isFinite(lat) || !isFinite(lng)) return;
          var popup = '<div class="dz-shops-map-popup">'
            + '<strong>' + escHtml(normalizeName(shop.name)) + '</strong>'
            + '<div>' + escHtml([shop.area, shop.city].filter(Boolean).join(", ") || "Seller location") + '</div>'
            + '<div style="margin-top:6px"><a href="' + escHtml(shop.shop_url || ("/shop/" + encodeURIComponent(shop.slug || ""))) + '">Open shop</a></div>'
            + '</div>';
          var marker = L.marker([lat, lng]).addTo(mapState.map).bindPopup(popup);
          mapState.markers.push(marker);
          bounds.push([lat, lng]);
        });
        var buyerLoc = cached || readCache();
        if (buyerLoc && isFinite(buyerLoc.lat) && isFinite(buyerLoc.lng)) {
          var buyerIcon = L.divIcon({ className: "dz-buyer-map-marker", html: "<span></span>", iconSize: [18, 18], iconAnchor: [9, 9] });
          mapState.buyerMarker = L.marker([buyerLoc.lat, buyerLoc.lng], { icon: buyerIcon }).addTo(mapState.map).bindPopup("Your location");
          mapState.buyerCircle = L.circle([buyerLoc.lat, buyerLoc.lng], {
            radius: 900,
            color: "#2563eb",
            fillColor: "#60a5fa",
            fillOpacity: 0.12,
            weight: 1
          }).addTo(mapState.map);
          bounds.push([buyerLoc.lat, buyerLoc.lng]);
        }
        if (bounds.length === 1) {
          mapState.map.setView(bounds[0], 13);
        } else {
          mapState.map.fitBounds(bounds, { padding: [28, 28] });
        }
        setTimeout(function () { try { mapState.map.invalidateSize(); } catch (e) {} }, 80);
        var sort = document.querySelector("[data-dz-shop-sort]");
        var sortValue = (sort && sort.value) || ((cached || readCache()) ? "distance" : "products");
        var label = shops.length + " mapped shop" + (shops.length === 1 ? "" : "s");
        setStatus(refs, label + (sortValue === "distance" ? " · Sorted by distance" : sortValue === "products" ? " · Sorted by stock depth" : " · Sorted by newest"));
        if (refs.link) {
          refs.link.hidden = false;
          refs.link.href = googleMapsUrl(shops[0].lat, shops[0].lng, normalizeName(shops[0].name));
        }
      });
    }

    function loadMapData() {
      var refs = ensureShell();
      if (!refs) return;
      fetch(API_BASE + "?" + currentParams(), { headers: { Accept: "application/json" } })
        .then(function (r) { return r.ok ? r.json() : { items: [] }; })
        .then(function (data) { renderMap((data && data.items) || []); })
        .catch(function () {
          setOverlay(refs, "Map failed to load. You can still browse the seller cards below.");
          setStatus(refs, "Map unavailable right now");
        });
    }

    function scheduleLoad() {
      clearTimeout(mapState.debounce);
      mapState.debounce = setTimeout(loadMapData, 220);
    }

    function wireControls() {
      [
        "[data-dz-shop-search]",
        "[data-dz-shop-city]",
        "[data-dz-shop-category]",
        "[data-dz-shop-sort]",
        "[data-dz-shop-hide-empty]"
      ].forEach(function (selector) {
        var el = document.querySelector(selector);
        if (!el || el.dataset.dzMapBound) return;
        el.dataset.dzMapBound = "true";
        el.addEventListener(selector === "[data-dz-shop-search]" ? "input" : "change", scheduleLoad);
      });
      window.addEventListener(EVENT_NAME, scheduleLoad);
    }

    function boot() {
      var refs = ensureShell();
      if (!refs) return;
      wireControls();
      loadMapData();
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
    setTimeout(boot, 1200);
  }

  // ── Floating "Deliver to" chip — bottom-right on every buyer-facing page ──
  // Skipped on checkout, account, and cart pages where Shopify owns the UI.
  function shouldSkipFloatingChip() {
    var p = location.pathname || "";
    return (
      p.indexOf("/checkouts/") === 0 ||
      p.indexOf("/checkout") === 0 ||
      p.indexOf("/account") === 0 ||
      p.indexOf("/cart") === 0
    );
  }

  function renderFloatingChip() {
    if (shouldSkipFloatingChip()) return;
    if (document.getElementById("dz-floating-loc")) return;
    var chip = document.createElement("button");
    chip.id = "dz-floating-loc";
    chip.type = "button";
    chip.setAttribute("data-dz-change-location", "");
    chip.setAttribute("aria-label", "Change delivery location");
    chip.style.cssText = [
      "position:fixed",
      "bottom:14px",
      "left:14px",
      "right:auto",
      "z-index:500",
      "background:rgba(43,27,12,.92)",
      "color:#fff",
      "border:1px solid rgba(255,255,255,.1)",
      "border-radius:999px",
      "padding:7px 12px",
      "font:600 11px system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
      "box-shadow:0 4px 16px rgba(0,0,0,.2)",
      "cursor:pointer",
      "display:flex",
      "align-items:center",
      "gap:5px",
      "max-width:calc(100vw - 28px)",
      "overflow:hidden",
      "text-overflow:ellipsis",
      "white-space:nowrap",
      "backdrop-filter:blur(6px)",
      "-webkit-backdrop-filter:blur(6px)"
    ].join(";");
    document.body.appendChild(chip);
    updateFloatingChip();
  }

  function updateFloatingChip() {
    var chip = document.getElementById("dz-floating-loc");
    if (!chip) return;
    var loc = cached || readCache();
    if (loc) {
      // Never show raw lat/lng — while reverse geocoding is still in flight, say "Your area".
      var label = loc.label || (loc.source === "pincode" && loc.pincode ? loc.pincode : "Your area");
      chip.innerHTML = "📍 <span><b>" + label + "</b></span><span style=\"opacity:.55;margin-left:5px;font-weight:500;\">Change</span>";
    } else {
      chip.innerHTML = "📍 <span style=\"font-weight:600;\">Set location</span>";
    }
  }

  window.addEventListener("dz:buyerlocation", updateFloatingChip);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderFloatingChip);
  } else {
    renderFloatingChip();
  }
  initNearbyShopsMap();
})();
